import { useEffect, useMemo, useState, type CSSProperties } from "react";
import GradientBlobContainer from "@/components/ui/container/GradientBlobContainer";
import {
  resolveBackgroundColor,
  resolveBackgroundImageUrl,
  resolveBrandingBackgroundImageStyle,
  resolveDesktopWallpaperMode,
  resolveWatermarkLogoUrl,
} from "@/components/branding/brandingUtils";
import {
  buildStickyWatermarkScope,
  readStickyBackgroundSnapshot,
} from "@/components/branding/brandingCacheSync";
import { BrandingWatermark } from "@/components/branding/BrandingWatermark";
import { watermarkPositionClasses } from "@/components/branding/BrandingPlacementPicker";
import { useResolvedBranding } from "@/components/branding/useResolvedBranding";
import { useOsWatermarkBranding } from "@/components/branding/useOsWatermarkBranding";
import { useAuth } from "@/context/AuthContext";
import { useColorSchemeWithFallback } from "@/context/ColorSchemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { Theme } from "@/config/themes";
import {
  ANIOS_THEME_UNDERLAY_DARK,
  ANIOS_THEME_UNDERLAY_LIGHT,
  resolveDesktopWallpaperUnderlay,
  resolveThemeWallpaperColors,
} from "@/config/themeWallpapers";
import {
  apiImageUrlNeedsSessionFetch,
  toProxyUrl,
} from "@/utils/imageUrl";
import {
  acquireSharedImageBlobUrl,
  releaseSharedImageBlobUrl,
} from "@/services/api/sharedImageBlobCache";
import type { EffectiveBranding } from "@/services/api/agentic-v2/types";

const BRANDING_IMAGE_UNDERLAY = ANIOS_THEME_UNDERLAY_DARK;

async function decodeBackgroundImage(src: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Background image failed to load"));
    img.src = src;
  });
}

function useBackgroundImageSrc(
  imageUrl: string | null | undefined,
): { src: string | null; isReady: boolean } {
  const [src, setSrc] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(() => !imageUrl);

  useEffect(() => {
    let cancelled = false;
    let proxiedForRelease: string | null = null;

    if (!imageUrl) {
      setSrc(null);
      setIsReady(true);
      return;
    }

    setSrc(null);
    setIsReady(false);

    async function load() {
      const proxied = toProxyUrl(imageUrl);
      if (!proxied) {
        if (!cancelled) setIsReady(true);
        return;
      }

      let resolvedSrc = proxied;
      if (apiImageUrlNeedsSessionFetch(proxied)) {
        proxiedForRelease = proxied;
        const resolved = await acquireSharedImageBlobUrl(proxied);
        if (cancelled) return;
        if (!resolved) {
          setIsReady(true);
          return;
        }
        resolvedSrc = resolved;
      }

      try {
        await decodeBackgroundImage(resolvedSrc);
      } catch {
        if (!cancelled) setIsReady(true);
        return;
      }

      if (!cancelled) {
        setSrc(resolvedSrc);
        setIsReady(true);
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (proxiedForRelease) {
        releaseSharedImageBlobUrl(proxiedForRelease);
      }
    };
  }, [imageUrl]);

  return { src, isReady };
}

function resolveBackgroundStyle(
  branding: EffectiveBranding | undefined,
  isDark: boolean,
  backgroundImageSrc: string | null,
): CSSProperties | undefined {
  const background = branding?.background;
  if (!background || background.mode === "theme") return undefined;

  if (background.mode === "color") {
    const color = resolveBackgroundColor(background, isDark);
    if (color) return { backgroundColor: color };
  }

  if (background.mode === "image" && backgroundImageSrc) {
    return resolveBrandingBackgroundImageStyle(backgroundImageSrc);
  }

  return undefined;
}

function useAniOsBrandingVisuals(isDark: boolean) {
  const { user } = useAuth();
  const stickyScope = useMemo(
    () => buildStickyWatermarkScope(user),
    [user?.company_id, user?.id],
  );
  const stickyBackground = useMemo(
    () => readStickyBackgroundSnapshot(stickyScope),
    [stickyScope],
  );
  const { data: branding, isFetched } = useResolvedBranding();
  const effectiveBackground = branding?.background ?? stickyBackground ?? undefined;
  const effectiveBranding = useMemo<EffectiveBranding | undefined>(() => {
    if (branding) return branding;
    if (!stickyBackground) return undefined;
    return { background: stickyBackground };
  }, [branding, stickyBackground]);

  const wallpaperMode = resolveDesktopWallpaperMode(effectiveBackground, isDark);
  const imageUrl = resolveBackgroundImageUrl(effectiveBackground, isDark);
  const { src: backgroundImageSrc, isReady: isBackgroundImageReady } =
    useBackgroundImageSrc(wallpaperMode === "image" ? imageUrl ?? null : null);

  const hasAuthoritativeBranding = isFetched;
  const hasBackgroundHint = Boolean(stickyBackground?.mode);
  const isBrandingResolved = hasAuthoritativeBranding || hasBackgroundHint;

  const customStyle = useMemo(() => {
    if (!isBrandingResolved || !effectiveBranding) return undefined;
    if (wallpaperMode === "image" && !isBackgroundImageReady) return undefined;
    return resolveBackgroundStyle(effectiveBranding, isDark, backgroundImageSrc);
  }, [
    backgroundImageSrc,
    wallpaperMode,
    effectiveBranding,
    isBackgroundImageReady,
    isBrandingResolved,
    isDark,
  ]);

  const underlayStyle = useMemo((): CSSProperties | undefined => {
    if (wallpaperMode === "image" && isBackgroundImageReady) return undefined;
    if (wallpaperMode === "color" && customStyle) return undefined;
    if (wallpaperMode === "theme") {
      return {
        backgroundColor: isDark
          ? ANIOS_THEME_UNDERLAY_DARK
          : ANIOS_THEME_UNDERLAY_LIGHT,
      };
    }
    return { backgroundColor: BRANDING_IMAGE_UNDERLAY };
  }, [wallpaperMode, customStyle, isBackgroundImageReady, isDark]);

  const watermarkPosition = branding?.watermark?.position ?? "bottom_right";

  return {
    branding,
    wallpaperMode,
    customStyle,
    isBrandingResolved,
    isDark,
    underlayStyle,
    watermarkPosition,
  };
}

/** Background colour/image/gradient behind the whole OS shell. */
export function AniOsBrandingBackground({
  displayedTheme,
  isDarkMode,
  isBackgroundFading,
}: {
  displayedTheme: Theme | undefined;
  isDarkMode: boolean;
  isBackgroundFading: boolean;
}) {
  const { wallpaperMode, customStyle, isBrandingResolved, underlayStyle } =
    useAniOsBrandingVisuals(isDarkMode);

  const themeUnderlayStyle = useMemo((): CSSProperties | undefined => {
    if (
      !isBrandingResolved ||
      wallpaperMode !== "theme" ||
      isDarkMode ||
      !displayedTheme
    ) {
      return underlayStyle;
    }
    return { backgroundColor: resolveDesktopWallpaperUnderlay(displayedTheme) };
  }, [
    wallpaperMode,
    displayedTheme,
    isBrandingResolved,
    isDarkMode,
    underlayStyle,
  ]);

  return (
    <>
      {themeUnderlayStyle ? (
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={themeUnderlayStyle}
          data-testid="anios-background-underlay"
        />
      ) : null}

      {customStyle ? (
        <div
          className={`absolute inset-0 z-0 pointer-events-none transition-opacity duration-500 ease-out ${
            isBackgroundFading ? "opacity-0" : "opacity-100"
          }`}
          style={customStyle}
          data-testid="anios-custom-background"
        />
      ) : null}

      {isBrandingResolved && wallpaperMode === "theme" && displayedTheme ? (
        <div
          className={`absolute inset-0 z-0 pointer-events-none transition-opacity duration-300 ease-in-out ${
            isBackgroundFading ? "opacity-0" : isDarkMode ? "opacity-40" : "opacity-100"
          }`}
        >
          <GradientBlobContainer
            key={`${displayedTheme.seed ?? displayedTheme.name}-${isDarkMode ? "dark" : "light"}`}
            seed={displayedTheme.seed ?? displayedTheme.name}
            themeColors={resolveThemeWallpaperColors(displayedTheme, isDarkMode)}
            dark={isDarkMode}
            surface="desktop"
            position="bl"
            className="w-full h-full"
            eager
            rasterize
          />
        </div>
      ) : null}
    </>
  );
}

/** Desktop watermark — sits above wallpaper, below app windows. */
export function AniOsBrandingWatermark() {
  const { isDark } = useColorSchemeWithFallback();
  const { branding, isLoading, isFetching } = useOsWatermarkBranding();

  if (!branding?.watermark?.enabled) {
    return null;
  }

  const watermarkPosition = branding.watermark?.position ?? "bottom_right";
  const logoUrl = resolveWatermarkLogoUrl(branding);
  const positionClass = watermarkPositionClasses(watermarkPosition);

  if (!logoUrl) {
    if (!isLoading && !isFetching) {
      return null;
    }
    return (
      <div
        className={`pointer-events-none absolute z-[1] flex items-center justify-center ${positionClass}`}
        data-testid="anios-brand-watermark-loading"
      >
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <BrandingWatermark
      branding={branding}
      isDark={isDark}
      useDefaultLogoWhenMissing={false}
      className={`pointer-events-none absolute z-[1] ${positionClass}`}
      testId="anios-brand-watermark"
    />
  );
}

/** @deprecated Prefer AniOsBrandingBackground + AniOsBrandingWatermark in OsLayout. */
export function AniOsBrandingLayer({
  displayedTheme,
  isDarkMode,
  isBackgroundFading,
}: {
  displayedTheme: Theme | undefined;
  isDarkMode: boolean;
  isBackgroundFading: boolean;
}) {
  return (
    <>
      <AniOsBrandingBackground
        displayedTheme={displayedTheme}
        isDarkMode={isDarkMode}
        isBackgroundFading={isBackgroundFading}
      />
      <AniOsBrandingWatermark />
    </>
  );
}

export function useBrandingAppearancePolicy() {
  const { data } = useResolvedBranding();
  return {
    allowUserAppearanceCustomization: data?.allow_user_appearance_customization ?? true,
    backgroundMode: data?.background?.mode ?? "theme",
  };
}
